/* Jointura PDP interactions: gallery sync, info tabs, and the single
   theme-owned add-to-cart path.

   ATC is deliberately NOT handled by Horizon's product-form-component. Horizon
   binds on: handlers to the document in the capture phase (component.js:278),
   so its add would run before any bubble-phase preventDefault here — two cart
   writes per tap (F2). The section renders a plain form; this module owns the
   submit, serialises the intent and reconciles against /cart.js. */
import { createAtcController } from '@theme/jt-atc-controller';
import { createCartReader } from '@theme/jt-cart-reader';
import { evaluateOfferInvariant } from '@theme/jt-offer-invariant';

(function () {
  'use strict';

  var cartReader = createCartReader({ maxAttempts: 4, baseDelayMs: 250, maxDelayMs: 1000 });

  function initProduct(root) {
    /* ---- gallery ---- */
    var slides = root.querySelectorAll('[data-jt-slides] .jt-slide');
    var thumbs = root.querySelectorAll('[data-jt-thumbs] .jt-thumb');
    var dotsWrap = root.querySelector('[data-jt-dots]');
    var dots = [];

    function show(i) {
      slides.forEach(function (s, j) { s.classList.toggle('jt-on', j === i); });
      thumbs.forEach(function (t, j) { t.classList.toggle('jt-sel', j === i); });
      dots.forEach(function (d, j) { d.classList.toggle('jt-on', j === i); });
    }

    if (dotsWrap) {
      slides.forEach(function (_, i) {
        var d = document.createElement('i');
        if (i === 0) d.classList.add('jt-on');
        d.addEventListener('click', function () { show(i); });
        dotsWrap.appendChild(d);
        dots.push(d);
      });
    }
    thumbs.forEach(function (t, i) {
      t.addEventListener('click', function () { show(i); });
    });

    /* Offer selection, quantity and prices are owned by Kaching Bundles
       (D-001 §0 rules 1 and 3). The theme deliberately holds no tier state and
       no price element.

       What used to live here was a tier picker plus a capture-phase submit guard
       that reasserted the theme's quantity over "whatever any third-party script
       (e.g. Kaching) wrote into the input". That guard was DQ-1 expressed in JS:
       it discarded the shopper's actual Kaching selection at submit time, which
       is why every tier collapsed to one price. Both are gone. */

    /* ---- info tabs ---- */
    var tabBtns = root.querySelectorAll('[data-jt-tabs] button');
    var panels = root.querySelectorAll('.jt-cf-panel');
    tabBtns.forEach(function (btn, i) {
      btn.addEventListener('click', function () {
        tabBtns.forEach(function (b) { b.classList.remove('jt-on'); });
        panels.forEach(function (p) { p.classList.remove('jt-on'); });
        btn.classList.add('jt-on');
        if (panels[i]) panels[i].classList.add('jt-on');
      });
    });
  }

  /* ------------------------------------------------------------------
     Meta/GA4 AddToCart signal.

     Horizon adds to cart with a plain fetch, which Shopify's web-pixel
     layer does not detect on this template — so no `product_added_to_cart`
     is published and Meta never records an AddToCart. We publish it
     ourselves, exactly ONCE per user tap (a flag set on click and cleared
     on the first cart:update), which also prevents the multi-fire problem
     the app-driven cart writes cause elsewhere on the store.
     ------------------------------------------------------------------ */
  function initAtcAnalytics(root) {
    var data = root.querySelector('[data-jt-analytics]');
    if (!data) return;
    var product;
    try { product = JSON.parse(data.textContent); } catch (e) { return; }

    var pending = null;

    var button = root.querySelector('.jt-atc-wrap button[name="add"]');
    if (button) {
      button.addEventListener('click', function () {
        /* The quantity is Kaching's, not ours. Kaching writes the selected
           tier's PAID units into this hidden input — that is exactly what the
           removed submit-time guard used to overwrite. Measured: the input reads
           "2" on a fresh load (the default "Buy 2 Get 1 Free" tier: 2 paid, 1
           free) while the theme renders value="1".

           Paid units, not offer size, is also the correct figure for revenue
           reporting: price x paid-units equals the cart total for every offer
           (1 -> ₹1,199, 2 -> ₹2,398, 3 -> ₹3,597). Reading the input keeps that
           correct without coupling to any Kaching DOM.

           Asserted by tests/browser/atc-acceptance.spec.js. */
        var qtyInput = root.querySelector('[data-jt-qty]');
        var qty = parseInt((qtyInput && qtyInput.value) || '1', 10);
        pending = { quantity: qty > 0 ? qty : 1 };
      });

    }

    /* No cart:update listener and no MutationObserver trigger any more. Both
       existed to catch Horizon's add, whose completion signal was unreliable.
       The controller now owns the add and calls publishAtc() exactly once, only
       after /cart.js has confirmed the mutation — so a publish can no longer
       fire for an add that silently failed. The payload below is unchanged. */
    return publishAtc;

    function publishAtc() {
      if (!pending) return;
      var qty = pending.quantity;
      pending = null;

      var amount = ((product.price || 0) * qty) / 100;
      var payload = {
        cartLine: {
          quantity: qty,
          cost: { totalAmount: { amount: amount, currencyCode: product.currency } },
          merchandise: {
            id: String(product.variant_id),
            price: { amount: (product.price || 0) / 100, currencyCode: product.currency },
            product: {
              id: String(product.id),
              title: product.title,
              vendor: product.vendor,
              type: product.type,
              url: product.url,
            },
            sku: product.sku,
            title: product.variant_title,
          },
        },
      };
      try {
        if (window.Shopify && window.Shopify.analytics && window.Shopify.analytics.publish) {
          window.Shopify.analytics.publish('product_added_to_cart', payload);
        }
      } catch (err) { /* analytics must never break the buy path */ }
    }
  }

  /* ------------------------------------------------------------------
     The single theme-owned add-to-cart path.
     ------------------------------------------------------------------ */
  function wireAtc(root, publishAtc) {
    var form = root.querySelector('.jt-atc-wrap form');
    var button = root.querySelector('.jt-atc-wrap button[name="add"]');
    var status = root.querySelector('[data-jt-atc-status]');
    if (!form || !button) return;

    /* Fail closed. Without the gift variant id the controller would reconcile
       against NaN, which never equals a variant id, so every add would report
       diet-plan-not-exactly-once and no shopper could ever buy. Refusing to
       wire is louder and safer than a buy button that silently always fails. */
    var giftId = Number(root.dataset.jtDietPlanVariant);
    if (!Number.isFinite(giftId) || giftId <= 0) {
      console.error('[jt] diet plan variant id missing or invalid — add-to-cart not wired');
      return;
    }

    var controller = createAtcController({
      dietPlanVariantId: giftId,
      reconcileTimeoutMs: 8000,
      pollIntervalMs: 500,

      submit: function () {
        /* FormData(form) — never a hand-built payload. The form carries the
           variant id and the quantity Kaching wrote there. Constructing the
           body ourselves would mean owning offer economics (D-001 §0 rule 1). */
        return fetch('/cart/add.js', {
          method: 'POST',
          body: new FormData(form),
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          credentials: 'same-origin',
        }).then(function (r) {
          if (!r.ok) throw new Error('add ' + r.status);
          return r.json();
        });
      },

      readCart: function () {
        return cartReader.read().then(function (result) {
          if (!result.ok) throw new Error(result.reason);
          return result.cart;
        });
      },

      publishEvent: function () { if (publishAtc) publishAtc(); },

      onStateChange: function (state) {
        button.disabled = state === 'locked';
        button.setAttribute('aria-busy', state === 'locked' ? 'true' : 'false');
        if (!status) return;
        status.hidden = state !== 'error';
        if (state === 'error') {
          status.textContent = 'We could not add that to your cart. Please try again.';
        }
      },
    });

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      controller.request().then(function (result) {
        if (!result || !result.ok) return;

        /* Entitlement check on the reconciled cart. This REPORTS; it does not
           block. D-003 established that every available pre-checkout block point
           leaks the shopper into native Shopify checkout still carrying the
           corrupted cart, which is forbidden — so a gate here would trade one
           defect for a worse one. Detecting corruption is real; claiming it is
           fixed would not be. F6 remains launch-blocking. */
        var invariant = evaluateOfferInvariant({ cart: result.cart, giftVariantId: giftId });
        if (!invariant.ok && status) {
          status.hidden = false;
          status.textContent =
            'Your free Diet Plan is not showing correctly in this cart. Please re-select your bundle above before checking out.';
        }

        afterAdd();
      });
    });
  }

  /* Post-add: the jt layout ships no theme cart drawer, so show the shopper her
     cart unless something else already will. Detect the app cart by its RUNTIME
     API, not its DOM classes — the previous '.kaching-cart-drawer, #kaching-cart'
     selectors were undocumented internals that a vendor rename would silently
     break, bouncing shoppers off the PDP mid-purchase. */
  function afterAdd() {
    var hasThemeDrawer = !!document.querySelector('cart-drawer-component');
    var hasAppCart = !!(window.kachingCartApi && typeof window.kachingCartApi.openCart === 'function');
    if (!hasThemeDrawer && !hasAppCart) {
      window.setTimeout(function () { window.location.href = '/cart'; }, 650);
    }
  }

  function boot() {
    document.querySelectorAll('[data-jt-product]').forEach(function (root) {
      initProduct(root);
      wireAtc(root, initAtcAnalytics(root));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
